import React, { useState, useCallback } from 'react';

interface TokenImageProps {
  src?: string;
  alt: string;
  symbol?: string;
  className?: string;
  width?: number;
  height?: number;
}

const TokenImage: React.FC<TokenImageProps> = ({
  src,
  alt,
  symbol,
  className = '',
  width = 24,
  height = 24,
}) => {
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const handleImageError = useCallback(() => {
    setImageError(true);
    setIsLoading(false);
  }, []);

  const handleImageLoad = useCallback(() => {
    setIsLoading(false);
    setImageError(false);
  }, []);

  // Generate placeholder based on symbol
  const generatePlaceholder = () => {
    const displaySymbol = symbol?.slice(0, 2).toUpperCase() || '??';
    const colors = [
      '#3B82F6',
      '#10B981',
      '#F59E0B',
      '#EF4444',
      '#8B5CF6',
      '#06B6D4',
      '#84CC16',
      '#F97316',
    ];

    let colorIndex = 0;
    if (symbol) {
      colorIndex =
        symbol.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) %
        colors.length;
    }

    const color = colors[colorIndex];

    return (
      <div
        className={`bitte-flex bitte-items-center bitte-justify-center bitte-rounded-full bitte-text-white bitte-font-semibold bitte-text-[8px] ${className}`}
        style={{
          backgroundColor: color,
          width: width,
          height: height,
          minWidth: width,
          minHeight: height,
        }}
      >
        {displaySymbol}
      </div>
    );
  };

  // Show placeholder if no src or if image failed to load
  if (!src || imageError) {
    return generatePlaceholder();
  }

  return (
    <div className={`bitte-relative ${className}`} style={{ width, height }}>
      {isLoading && (
        <div
          className="bitte-absolute bitte-inset-0 bitte-bg-gray-600 bitte-rounded-full bitte-animate-pulse"
          style={{ width, height }}
        />
      )}
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        className={`bitte-rounded-full ${isLoading ? 'bitte-opacity-0' : 'bitte-opacity-100'} bitte-transition-opacity`}
        onError={handleImageError}
        onLoad={handleImageLoad}
        style={{ width, height }}
      />
    </div>
  );
};

export default TokenImage;
